import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PublicAuthLayout, { PublicAuthAlert, PublicAuthSpinner } from '../components/PublicAuthLayout';
import api, { verifyEmail } from '../services/api';
import { useAuth } from '../hooks/useAuth';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user } = useAuth();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');
  const verifiedRef = useRef(false);

  async function checkAlreadyVerified(): Promise<boolean> {
    try {
      const res = await api.get('/auth/me');
      const me = res.data?.user;
      return !!me?.email_verified_at;
    } catch {
      return false;
    }
  }

  useEffect(() => {
    if (!token) {
      setError('Invalid verification link. The token is missing.');
      setStatus('error');
      return;
    }

    if (verifiedRef.current) return;
    verifiedRef.current = true;

    verifyEmail(token)
      .then(() => {
        if (user) {
          sessionStorage.removeItem(`pending_verification.${user.id}`);
          sessionStorage.removeItem(`verification_dismissed.${user.id}`);
          sessionStorage.removeItem(`verification_url.${user.id}`);
        }
        setStatus('success');
      })
      .catch(async (err) => {
        const alreadyVerified = await checkAlreadyVerified();
        if (alreadyVerified) {
          if (user) {
            sessionStorage.removeItem(`pending_verification.${user.id}`);
            sessionStorage.removeItem(`verification_dismissed.${user.id}`);
            sessionStorage.removeItem(`verification_url.${user.id}`);
          }
          setStatus('success');
          return;
        }
        setError(err.response?.data?.error || 'This verification link is invalid or has expired.');
        setStatus('error');
      });
  }, [token, user]);

  return (
    <PublicAuthLayout
      asideBody="Verification protects your workspace and helps Seedronics keep account access tied to the right company email."
      asideTitle={status === 'success' ? 'Email confirmed.' : 'Checking your verification link.'}
      eyebrow="Email verification"
      footer={
        <>
          Need another email? <Link to="/login">Resend from sign in</Link>
        </>
      }
      highlights={['Account protection', 'Workspace access', 'Team invite readiness']}
      title={status === 'success' ? 'Your email is verified.' : 'Verifying your Seedronics account.'}
      subtitle={status === 'loading' ? 'This should only take a moment.' : 'You can continue to your workspace when ready.'}
    >
      {status === 'loading' && (
        <div className="sd-public-auth-loading">
          <PublicAuthSpinner />
          <span>Verifying email...</span>
        </div>
      )}

      {status === 'success' && (
        <>
          <PublicAuthAlert tone="success">
            Your email address has been verified. Your Seedronics workspace is ready.
          </PublicAuthAlert>
          <Link className="sd-public-auth-submit" to={user ? '/dashboard' : '/login'}>
            {user ? 'Go to dashboard' : 'Sign in'}
          </Link>
        </>
      )}

      {status === 'error' && (
        <>
          <PublicAuthAlert>{error}</PublicAuthAlert>
          <Link className="sd-public-auth-submit" to={user ? '/dashboard' : '/login'}>
            {user ? 'Back to dashboard' : 'Back to sign in'}
          </Link>
        </>
      )}
    </PublicAuthLayout>
  );
}
